import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { EventBus } from '../core/EventBus';

let destructibleIdCounter = 0;

export interface DestructibleConfig {
  position: THREE.Vector3;
  size?: THREE.Vector3;
  health?: number;
  points?: number;
  color?: THREE.Color;
  glowColor?: THREE.Color;
}

export class Destructible {
  public readonly id: string;
  public readonly mesh: THREE.Group;
  private body: CANNON.Body;
  private physicsWorld: PhysicsWorld;
  private health: number;
  private maxHealth: number;
  private isDestroyed: boolean = false;
  private debris: THREE.Mesh[] = [];

  constructor(
    scene: THREE.Scene,
    physicsWorld: PhysicsWorld,
    config: DestructibleConfig
  ) {
    this.id = `destructible_${destructibleIdCounter++}`;
    this.physicsWorld = physicsWorld;
    this.health = config.health ?? 50;
    this.maxHealth = this.health;

    const size = config.size ?? new THREE.Vector3(2, 2, 2);
    const color = config.color ?? new THREE.Color(0x2a2a4e);
    const glowColor = config.glowColor ?? new THREE.Color(0xff00ff);

    // Create mesh group
    this.mesh = this.createMesh(size, color, glowColor);
    this.mesh.position.copy(config.position);
    scene.add(this.mesh);

    // Store hit data on mesh for collision detection
    this.mesh.userData = {
      id: this.id,
      health: this.health,
      hitRadius: Math.max(size.x, size.y, size.z) / 2,
      points: config.points ?? 100,
    };

    // Create physics body
    this.body = this.createBody(config.position, size);
    physicsWorld.addBody(this.body);

    // Listen for damage events
    EventBus.on('target:damaged', (data) => {
      if (data.id === this.id) {
        this.health = data.healthRemaining;
        this.mesh.userData.health = this.health;
        this.onDamaged();
      }
    });

    EventBus.on('target:destroyed', (data) => {
      if (data.id === this.id) {
        this.destroy(scene);
      }
    });
  }

  private createMesh(
    size: THREE.Vector3,
    color: THREE.Color,
    glowColor: THREE.Color
  ): THREE.Group {
    const group = new THREE.Group();

    // Main box
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    const material = new THREE.MeshStandardMaterial({
      color: color,
      metalness: 0.7,
      roughness: 0.3,
    });
    const box = new THREE.Mesh(geometry, material);
    box.position.y = size.y / 2;
    group.add(box);

    // Neon edges
    const edgeGeometry = new THREE.EdgesGeometry(geometry);
    const edgeMaterial = new THREE.LineBasicMaterial({
      color: glowColor,
      linewidth: 2,
    });
    const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
    edges.position.y = size.y / 2;
    group.add(edges);

    // Glowing core
    const coreGeometry = new THREE.BoxGeometry(
      size.x * 0.3,
      size.y * 0.3,
      size.z * 0.3
    );
    const coreMaterial = new THREE.MeshStandardMaterial({
      color: glowColor,
      emissive: glowColor,
      emissiveIntensity: 2,
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    core.position.y = size.y / 2;
    core.userData.isCore = true;
    group.add(core);

    return group;
  }

  private createBody(position: THREE.Vector3, size: THREE.Vector3): CANNON.Body {
    const shape = new CANNON.Box(
      new CANNON.Vec3(size.x / 2, size.y / 2, size.z / 2)
    );
    const body = new CANNON.Body({
      mass: 0, // Static
      shape: shape,
      position: new CANNON.Vec3(position.x, position.y + size.y / 2, position.z),
    });
    body.collisionFilterGroup = 8; // Destructible group
    return body;
  }

  private onDamaged(): void {
    // Flash effect
    const healthPercent = this.health / this.maxHealth;

    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData.isCore) {
        const material = child.material as THREE.MeshStandardMaterial;
        // Pulse the core faster as health decreases
        material.emissiveIntensity = 2 + (1 - healthPercent) * 3;
      }
    });

    // Shake effect
    const originalPos = this.mesh.position.clone();
    const shake = () => {
      this.mesh.position.x = originalPos.x + (Math.random() - 0.5) * 0.2;
      this.mesh.position.z = originalPos.z + (Math.random() - 0.5) * 0.2;
    };
    shake();
    setTimeout(() => {
      this.mesh.position.copy(originalPos);
    }, 50);
  }

  private destroy(scene: THREE.Scene): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Create debris explosion
    this.createDebris(scene);

    // Remove main mesh
    scene.remove(this.mesh);

    // Remove physics body
    this.physicsWorld.removeBody(this.body);
  }

  private createDebris(scene: THREE.Scene): void {
    const position = this.mesh.position.clone();
    const debrisCount = 8;

    for (let i = 0; i < debrisCount; i++) {
      const size = 0.2 + Math.random() * 0.3;
      const geometry = new THREE.BoxGeometry(size, size, size);
      const material = new THREE.MeshStandardMaterial({
        color: Math.random() > 0.5 ? 0xff00ff : 0x00ffff,
        emissive: Math.random() > 0.5 ? 0xff00ff : 0x00ffff,
        emissiveIntensity: 1,
      });
      const debris = new THREE.Mesh(geometry, material);

      // Random position around center
      debris.position.set(
        position.x + (Math.random() - 0.5) * 2,
        position.y + 1 + Math.random() * 2,
        position.z + (Math.random() - 0.5) * 2
      );

      // Random rotation
      debris.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );

      // Store velocity for animation
      debris.userData.velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 10,
        5 + Math.random() * 5,
        (Math.random() - 0.5) * 10
      );
      debris.userData.angularVelocity = new THREE.Vector3(
        (Math.random() - 0.5) * 5,
        (Math.random() - 0.5) * 5,
        (Math.random() - 0.5) * 5
      );
      debris.userData.lifetime = 2;
      debris.userData.spawnTime = performance.now() / 1000;

      scene.add(debris);
      this.debris.push(debris);
    }

    // Animate debris (will be cleaned up after lifetime)
    this.animateDebris(scene);
  }

  private animateDebris(scene: THREE.Scene): void {
    const gravity = -20;
    const animate = () => {
      const currentTime = performance.now() / 1000;
      let activeDebris = false;

      for (const debris of this.debris) {
        const age = currentTime - debris.userData.spawnTime;
        if (age > debris.userData.lifetime) {
          scene.remove(debris);
          continue;
        }

        activeDebris = true;

        // Apply gravity
        debris.userData.velocity.y += gravity * 0.016;

        // Update position
        debris.position.add(
          debris.userData.velocity.clone().multiplyScalar(0.016)
        );

        // Update rotation
        debris.rotation.x += debris.userData.angularVelocity.x * 0.016;
        debris.rotation.y += debris.userData.angularVelocity.y * 0.016;
        debris.rotation.z += debris.userData.angularVelocity.z * 0.016;

        // Fade out
        const material = debris.material as THREE.MeshStandardMaterial;
        material.opacity = 1 - age / debris.userData.lifetime;
        material.transparent = true;

        // Stop at ground
        if (debris.position.y < 0.1) {
          debris.position.y = 0.1;
          debris.userData.velocity.y *= -0.3;
          debris.userData.velocity.x *= 0.8;
          debris.userData.velocity.z *= 0.8;
        }
      }

      if (activeDebris) {
        requestAnimationFrame(animate);
      } else {
        this.debris = [];
      }
    };

    requestAnimationFrame(animate);
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }
}
